import { useQuery } from "@tanstack/react-query";
import { Copy, Edit, MinusIcon, Plus, PlusIcon, RefreshCw, Trash2 } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Combobox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import { getPaymentGateways } from "@/pages/payment-gateways/payment-gateways.api";
import {
  EProductType,
  TProduct,
  TProductWithCapitalRange,
} from "@/pages/products/products.type";
import { isInfinitySyncedProduct } from "@/pages/products/products-sync-origin";
import { formatPrice } from "@/utils/monetary-formatter";

import {
  TClientProductsFormData,
  TClientProductsFormSearchData,
} from "../../clients.schema";
import { PlanoModal } from "./modal-plan-form";

export interface IClientProductsFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TClientProductsFormData>;
  searchForm: UseFormReturn<TClientProductsFormSearchData>;
  onSubmit: (values: TClientProductsFormData) => void;
  filteredProducts: TProduct[];
  handleSearchProducts: () => void;
  clearProductsFilter: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isEditing?: boolean;
  submitUpdatePlan?: (index: number) => void;
  clientId?: string;
  projectType?: "affinity" | "affiliate";
  projectPartners?: Array<{
    id: string;
    name: string;
    commission_percentage: number;
    level_hierarchy: number;
  }>;
  projectFather?: {
    id: string;
    name: string;
  };
  projectParticipantOrganizations?: Array<{
    id: string;
    name: string;
  }>;
  projectLevelHierarchical?: number;
  /** Catálogo completo (resolve gateway do primeiro item selecionado no plano). */
  productsCatalog?: TProduct[];
}

export const ClientProductsForm: React.FC<IClientProductsFormProps> = ({
  open,
  form,
  searchForm,
  onSubmit,
  filteredProducts,
  handleSearchProducts,
  clearProductsFilter,
  activeTab,
  setActiveTab,
  isEditing,
  submitUpdatePlan,
  clientId,
  projectType,
  projectPartners,
  projectFather,
  projectParticipantOrganizations,
  projectLevelHierarchical,
  productsCatalog = [],
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ clientId?: string }>();
  const autoSubmittedProjectPlanRef = React.useRef<string | null>(null);
  const autoSubmittedNewPlanRef = React.useRef<boolean>(false);


  // Obter clientId da prop ou dos parâmetros da URL
  const effectiveClientId = clientId || params.clientId;

  // Estados para a modal
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingPlanoIndex, setEditingPlanoIndex] = React.useState<
    number | undefined
  >(undefined);
  const [openRowId, setOpenRowId] = React.useState<string | null>(null);

  // Processar retorno da página de formulário
  React.useEffect(() => {
    const state = location.state as {
      savedPlanData?: any;
      editingPlanoIndex?: number;
      fromPlans?: boolean;
      planId?: string;
    } | null;

    if (!state?.savedPlanData) {
      // Resetar ref quando não houver savedPlanData
      autoSubmittedNewPlanRef.current = false;
      return;
    }

    const savedData = state.savedPlanData;
    let timeoutId: number | undefined;
    const currentPlanos = form.getValues("planos") || [];
    let index = state.editingPlanoIndex;

    // Se estiver editando (fromPlans e planId presentes), mas não tiver index, tentar encontrar o plano pelo ID
    if (index === undefined && state?.fromPlans && state?.planId) {
      const planIndex = currentPlanos.findIndex((p: any) => p.id === state.planId);
      if (planIndex !== -1) {
        index = planIndex;
      }
    }

    const basePlanData = {
      name: savedData.name,
      description: savedData.description,
      plan_features: Array.isArray(savedData.plan_features)
        ? savedData.plan_features
            .flat()
            .filter((feature: any) => typeof feature === "string" && feature.trim() !== "")
        : [],
      markup: savedData.markup,
      markup_mode: savedData.markup_mode,
      products: savedData.products,
      commission_distribution: savedData.commission_distribution,
      commission_lifetime_other_installments: savedData.commission_lifetime_other_installments,
      plan_price: savedData.products.reduce(
        (total: number, product: any) => total + (product.premium_value || 0),
        0
      ),
      installments:
        projectType === "affinity" || projectType === "affiliate"
          ? (savedData.installments || [])
          : undefined,
      otherInstallments:
        projectType === "affinity" || projectType === "affiliate"
          ? (savedData.otherInstallments || undefined)
          : undefined,
      affiliateCommissions:
        projectType === "affiliate" ? (savedData.affiliateCommissions || {}) : undefined,
      affiliateOtherCommissions:
        projectType === "affiliate" ? (savedData.affiliateOtherCommissions || {}) : undefined,
    };

    if (index === undefined) {
      const newPlano = {
        id: "plano-" + Date.now(),
        ...basePlanData,
      };
      form.setValue("planos", [...currentPlanos, newPlano], { shouldDirty: true });
      
      // Se vier do plan-form-page e não estiver editando, chamar onSubmit automaticamente
      // Isso salva o plano automaticamente após retornar do formulário
      if (state?.savedPlanData && !isEditing && typeof onSubmit === "function" && !autoSubmittedNewPlanRef.current) {
        autoSubmittedNewPlanRef.current = true;
        // Usar um timeout para garantir que o setValue foi processado
        timeoutId = window.setTimeout(() => {
          const formData = form.getValues();
          try {
            onSubmit(formData);
          } catch (error) {
            // Silenciosamente ignorar erros
          }
        }, 200);
      }
    } else {
      const planoAtual = form.getValues(`planos.${index}` as any);
      const novoPlano = {
        ...planoAtual,
        ...basePlanData,
      };
      form.setValue(`planos.${index}` as any, novoPlano, { shouldDirty: true });

      // Se estiver editando um plano de projeto, chamar submitUpdatePlan automaticamente UMA vez
      if (isEditing && typeof submitUpdatePlan === "function" && state?.fromPlans) {
        const planId = state?.planId;
        if (planId && autoSubmittedProjectPlanRef.current !== planId) {
          autoSubmittedProjectPlanRef.current = planId;
          timeoutId = window.setTimeout(() => {
            submitUpdatePlan(index as number);
          }, 100);
        }
      }
    }

    // Limpar o state para evitar reprocessamento (NÃO retornar antes disso)
    // Mas fazer isso DEPOIS de configurar o timeout, para não perder o state
    const currentTimeoutId = timeoutId;
    if (currentTimeoutId !== undefined) {
      // Se houver timeout configurado, limpar o state depois que ele executar
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 300);
    } else {
      // Se não houver timeout, limpar imediatamente
      navigate(location.pathname, { replace: true, state: {} });
    }

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [location.state, form, isEditing, submitUpdatePlan, onSubmit, navigate, location.pathname, projectType]);

  // Estado para produtos selecionados com capital ranges
  const [
    selectedProductsWithCapitalRanges,
    setSelectedProductsWithCapitalRanges,
  ] = React.useState<TProductWithCapitalRange[]>([]);

  const { data: paymentGateways } = useQuery({
    queryKey: ["payment-gateways"],
    queryFn: () => getPaymentGateways(false),
  });

  const productCatalog = React.useMemo(
    () => (productsCatalog.length > 0 ? productsCatalog : filteredProducts),
    [productsCatalog, filteredProducts],
  );

  const planGatewayLockId = React.useMemo(() => {
    const first = selectedProductsWithCapitalRanges[0];
    if (!first) return undefined;
    const row = productCatalog.find((p) => p.id === first.product_id);
    const gid = row?.payment_gateway_id;
    if (gid == null || String(gid).trim() === "") return undefined;
    return gid;
  }, [selectedProductsWithCapitalRanges, productCatalog]);

  const lockedGatewayLabel = React.useMemo(() => {
    if (!planGatewayLockId) return "";
    const g = paymentGateways?.find((x) => x.id === planGatewayLockId);
    return g?.name || g?.slug || planGatewayLockId;
  }, [planGatewayLockId, paymentGateways]);

  const productsForPlanBuilderTable = React.useMemo(() => {
    const list = filteredProducts || [];
    if (!planGatewayLockId) return list;
    return list.filter((p) => p.payment_gateway_id === planGatewayLockId);
  }, [filteredProducts, planGatewayLockId]);


  const handleRowToggle = (productId: string) => {
    setOpenRowId(openRowId === productId ? null : productId);
  };

  // Função para selecionar/deselecionar um produto com capital range
  const handleProductSelection = (
    product: TProduct,
    capitalRange: {
      id: string;
      product_id: string;
      capital_value: number;
      premium_value: number;
      description: string;
      minimum_price?: number | null;
      plan_name?: string | null;
    }
  ) => {
    if (
      planGatewayLockId &&
      product.payment_gateway_id !== planGatewayLockId
    ) {
      toast({
        title: t("generic.error"),
        description: t("plansForm.gatewayMismatchToast"),
        variant: "destructive",
      });
      return;
    }

    const productWithCapitalRange: TProductWithCapitalRange = {
      id: capitalRange.id,
      product_id: product.id,
      name: product.name || "",
      capital_value: capitalRange.capital_value,
      premium_value: capitalRange.premium_value,
      description: capitalRange.description || "",
      type: product.type,
      type_of_vigency: product.type_of_vigency,
      minimum_price: capitalRange.minimum_price ?? null,
      plan_name: capitalRange.plan_name ?? null,
    };

    setSelectedProductsWithCapitalRanges((prev) => {
      const isAlreadySelected = prev.some(
        (item) => item.id === productWithCapitalRange.id
      );

      if (isAlreadySelected) {
        // Remove o produto se já estiver selecionado
        return prev.filter((item) => item.id !== productWithCapitalRange.id);
      } else {
        // Adiciona o produto se não estiver selecionado
        return [...prev, productWithCapitalRange];
      }
    });
  };

  const isProductSelected = (capitalRangeId: string) => {
    return selectedProductsWithCapitalRanges.some(
      (item) => item.id === capitalRangeId
    );
  };

  const [tempPlano, setTempPlano] = React.useState<{
    name: string;
    description: string;
    plan_features: string[];
    markup?: number;
    markup_mode?: "percentage" | "real_currency";
    products: Array<{
      id: string;
      capital_value?: number;
      premium_value?: number;
      description?: string;
      insurance_policy_extra_price_id?: string | null;
    }>;
    commission_distribution: {
      agency: number;
      commission: number;
      position: number;
      // Removidos os campos markup individuais das comissões
    }[];
    commission_lifetime_other_installments?: number;
  }>({
    name: "",
    description: "",
    plan_features: [],
    markup: 0,
    markup_mode: "percentage",
    products: [],
    commission_distribution: [{ agency: 0, commission: 0, position: 0 }],
    commission_lifetime_other_installments: 0,
  });

  const productsForModal = React.useMemo(() => {
    const result: TProductWithCapitalRange[] = [];

    // 1) Produtos selecionados na aba Produtos
    const fromSelection = selectedProductsWithCapitalRanges || [];
    result.push(...fromSelection);

    if (editingPlanoIndex !== undefined) {
      const plano = form.watch(`planos.${editingPlanoIndex}`);
      const fallbackVigency = "lifetime" as any;

      // Buscar os dados corretos do produto base (product_capital_ranges)
      const fromPlan = (plano?.products || []).map((p: any) => {
        // Buscar o produto original
        let originalProduct = filteredProducts.find(
          (fp) => fp.id === p.product_id
        );

        if (!originalProduct) {
          originalProduct = filteredProducts.find((fp) =>
            fp.capital_ranges?.some((range) => range.id === p.id)
          );
        }

        // Para assistências, buscar o capital_range correto pela description
        let planName = p.plan_name;
        let minimumPrice = p.minimum_price;

        if (originalProduct && p.type === EProductType.INSURANCE_ASSISTANCE) {
          const matchingRange = originalProduct.capital_ranges?.find(
            (range) => {
              const rangeDescription = range.description;
              // Buscar pela description
              if (
                rangeDescription &&
                p.description &&
                rangeDescription === p.description
              ) {
                return true;
              }
              return false;
            }
          );

          if (matchingRange) {
            // Pegar os valores corretos do capital_range
            planName = (matchingRange as any).plan_name || p.plan_name;
            minimumPrice =
              (matchingRange as any).minimum_price || p.minimum_price;
          }
        }

        return {
          id: p.id,
          product_id: p.product_id || p.id,
          name: p.name || "",
          type: p.type,
          capital_value: p.capital_value,
          premium_value: p.premium_value, // Valor de venda do plano
          description: p.description,
          type_of_vigency: p.type_of_vigency || fallbackVigency,
          minimum_price: minimumPrice, // Preço mínimo do capital_range
          plan_name: planName, // Nome correto do capital_range
          insurance_policy_extra_price_id:
            p.insurance_policy_extra_price_id || null,
        };
      }) as TProductWithCapitalRange[];
      result.push(...fromPlan);
    }

    const unique = new Map<string, TProductWithCapitalRange>();
    for (const item of result) {
      if (!unique.has(item.id)) unique.set(item.id, item);
    }
    return Array.from(unique.values());
  }, [
    selectedProductsWithCapitalRanges,
    editingPlanoIndex,
    form,
    filteredProducts,
  ]);

  const handleOpenNewPlanoModal = () => {
    // Verificar se clientId é válido (não undefined, null ou string vazia)
    if (!effectiveClientId || effectiveClientId.trim() === "") {
      // Fallback para o modal se não houver clientId
      setEditingPlanoIndex(undefined);
      setTempPlano({
        name: "",
        description: "",
        plan_features: [],
        markup: 0,
        markup_mode: "percentage",
        products: [],
        commission_distribution: [{ agency: 0, commission: 0, position: 0 }],
        commission_lifetime_other_installments: 0,
      });
      setModalOpen(true);
      return;
    }

    // Navegar para a nova página
    const navState = location.state as any;
    navigate(`/clients/${effectiveClientId}/plan-form`, {
      state: {
        selectedProducts: productsForModal,
        editingPlanoIndex: undefined,
        returnPath: location.pathname,
        clientId: effectiveClientId,
        projectType: projectType,
        projectPartners: projectPartners,
        projectFather: projectFather,
        projectParticipantOrganizations: projectParticipantOrganizations,
        projectLevelHierarchical: projectLevelHierarchical,
        // Preservar contexto de edição de plano de projeto (quando vier do ProjectDetail -> CreatePlanPage)
        fromPlans: navState?.fromPlans,
        planId: navState?.planId,
      },
    });
  };

  const handleEditPlano = (index: number) => {
    const planoExistente = form.watch(`planos.${index}`);

    // Verificar se clientId é válido (não undefined, null ou string vazia)
    if (!effectiveClientId || effectiveClientId.trim() === "") {
      // Fallback para o modal se não houver clientId
      setEditingPlanoIndex(index);
      if (planoExistente) {
        const tempPlanoData: any = {
          name: planoExistente.name || "",
          description: planoExistente.description || "",
          plan_features: Array.isArray(planoExistente.plan_features)
            ? planoExistente.plan_features.flat()
            : [],
          markup: planoExistente.markup ?? 0,
          markup_mode: planoExistente.markup_mode ?? "percentage",
          products: planoExistente.products || [],
          commission_distribution: (
            planoExistente.commission_distribution || [
              { agency: 0, commission: 0, position: 0 },
            ]
          ).map((item, idx) => ({
            agency: item.agency ?? 0,
            commission: item.commission ?? 0,
            position: item.position !== undefined ? item.position : idx,
          })),
          commission_lifetime_other_installments:
            planoExistente.commission_lifetime_other_installments || 0,
          selectedPartners: (planoExistente as any).selectedPartners || {},
        };
        
        // Incluir installments e otherInstallments se for projeto de afinidade
        if (projectType === "affinity") {
          tempPlanoData.installments = (planoExistente as any).installments || [];
          tempPlanoData.otherInstallments = (planoExistente as any).otherInstallments || {
            mainPartner: {
              id: projectFather?.id || "",
              name: projectFather?.name || "",
              commission: 0,
              markup: 0,
            },
            participatingPartners: (projectParticipantOrganizations || []).map((org) => ({
              id: org.id,
              name: org.name,
              commission: 0,
              markup: 0,
            })),
          };
        }
        
        setTempPlano(tempPlanoData);
      }
      setModalOpen(true);
      return;
    }

    // Navegar para a nova página com dados do plano
    const tempPlanoData: any = planoExistente
      ? {
          name: planoExistente.name || "",
          description: planoExistente.description || "",
          plan_features: Array.isArray(planoExistente.plan_features)
            ? planoExistente.plan_features.flat()
            : [],
          markup: planoExistente.markup ?? 0,
          markup_mode: planoExistente.markup_mode ?? "percentage",
          products: (planoExistente.products || []).map((p: any) => ({
            id: p.id,
            product_id: p.product_id || p.id,
            name: p.name || "",
            type: p.type || "",
            capital_value: p.capital_value || 0,
            premium_value: p.premium_value || 0,
            description: p.description || "",
            minimum_price: p.minimum_price ?? null,
            plan_name: p.plan_name ?? null,
            insurance_policy_extra_price_id: p.insurance_policy_extra_price_id ?? null,
          })),
          commission_distribution: (
            planoExistente.commission_distribution || [
              { agency: 0, commission: 0, position: 0 },
            ]
          ).map((item, idx) => ({
            agency: item.agency ?? 0,
            commission: item.commission ?? 0,
            position: item.position !== undefined ? item.position : idx,
          })),
          commission_lifetime_other_installments:
            planoExistente.commission_lifetime_other_installments || 0,
        }
      : undefined;
    
    // Incluir installments e otherInstallments se for projeto de afinidade ou afiliados
    if ((projectType === "affinity" || projectType === "affiliate") && tempPlanoData) {
      // Preservar installments e otherInstallments do plano existente
      const existingInstallments = (planoExistente as any).installments;
      const existingOtherInstallments = (planoExistente as any).otherInstallments;
      tempPlanoData.installments = existingInstallments || [];
      // Só criar otherInstallments default se não existir E não estiver editando (index === undefined)
      if (existingOtherInstallments) {
        tempPlanoData.otherInstallments = existingOtherInstallments;
      } else if (index === undefined && projectFather) {
        // Criar apenas para novos planos
        tempPlanoData.otherInstallments = {
          mainPartner: {
            id: projectFather.id,
            name: projectFather.name,
            commission: 0,
            markup: 0,
          },
          participatingPartners: (projectParticipantOrganizations || []).map((org) => ({
            id: org.id,
            name: org.name,
            commission: 0,
            markup: 0,
          })),
        };
      }
    }

    // Garantir que productsForModal inclua os produtos do plano quando estiver editando
    const productsToPass = index !== undefined 
      ? productsForModal 
      : selectedProductsWithCapitalRanges;

    // Determinar o returnPath correto
    // Se estiver dentro de um projeto, voltar para a página de criar plano do projeto
    // Se não, voltar para a página de planos do cliente
    let correctReturnPath = location.pathname;
    
    // Se a URL atual contém /projects/, significa que estamos dentro de um projeto
    if (location.pathname.includes('/projects/')) {
      // Extrair projectId da URL atual
      const pathParts = location.pathname.split('/');
      const projectIndex = pathParts.indexOf('projects');
      if (projectIndex !== -1 && pathParts[projectIndex + 1]) {
        const projectId = pathParts[projectIndex + 1];
        correctReturnPath = `/clients/${effectiveClientId}/projects/${projectId}/create-plan`;
      }
    }
    
    const navState = location.state as any;
    navigate(`/clients/${effectiveClientId}/plan-form`, {
      state: {
        selectedProducts: productsToPass,
        editingPlanoIndex: index,
        initialData: tempPlanoData,
        returnPath: correctReturnPath,
        clientId: effectiveClientId,
        projectType: projectType,
        projectPartners: projectPartners,
        projectFather: projectFather,
        projectParticipantOrganizations: projectParticipantOrganizations,
        projectLevelHierarchical: projectLevelHierarchical,
        // Preservar contexto de edição de plano de projeto (quando vier do ProjectDetail -> CreatePlanPage)
        fromPlans: navState?.fromPlans,
        planId: navState?.planId,
      },
    });
  };

  // Função para salvar plano
  const handleSavePlano = () => {
    if (editingPlanoIndex === undefined) {
      // Novo plano - usar dados do estado local
      const planoName = tempPlano.name;
      const planoDescription = tempPlano.description;
      const planoProducts = tempPlano.products;
      const planoCommissions = tempPlano.commission_distribution;
      const selectedPartners = (tempPlano as any).selectedPartners || {};

      // Só cria o plano se tiver nome E pelo menos um produto selecionado
      if (planoName && planoName.trim() !== "" && planoProducts.length > 0) {
        // Adicionar plano ao formulário
        const currentPlanos = form.watch("planos") || [];
        const newPlano = {
          id: "plano-" + Date.now(),
          name: planoName,
          description: planoDescription,
          plan_features: Array.isArray(tempPlano.plan_features)
            ? tempPlano.plan_features
                .flat()
                .filter(
                  (feature) =>
                    typeof feature === "string" && feature.trim() !== ""
                )
            : [],
          markup: tempPlano.markup,
          markup_mode: tempPlano.markup_mode,
          products: planoProducts,
          commission_distribution: planoCommissions,
          commission_lifetime_other_installments:
            tempPlano.commission_lifetime_other_installments,
          plan_price: planoProducts.reduce(
            (total, product) => total + (product.premium_value || 0),
            0
          ),
          selectedPartners: projectType === "affinity" ? selectedPartners : undefined,
          installments:
            projectType === "affinity" || projectType === "affiliate"
              ? tempPlano.installments
              : undefined,
          otherInstallments:
            projectType === "affinity" || projectType === "affiliate"
              ? tempPlano.otherInstallments
              : undefined,
        };

        form.setValue("planos", [...currentPlanos, newPlano]);

        // Fechar modal
        setModalOpen(false);
      }
    } else {
      const planoName = tempPlano.name;
      const planoDescription = tempPlano.description;
      const planoProducts = tempPlano.products;
      const planoCommissions = tempPlano.commission_distribution;
      const selectedPartners = (tempPlano as any).selectedPartners || {};

      if (planoName && planoName.trim() !== "" && planoProducts.length > 0) {
        const planoAtual = form.watch(`planos.${editingPlanoIndex}`);

        const novoPlano = {
          ...planoAtual,
          name: planoName,
          description: planoDescription,
          plan_features: Array.isArray(tempPlano.plan_features)
            ? tempPlano.plan_features
                .flat()
                .filter(
                  (feature) =>
                    typeof feature === "string" && feature.trim() !== ""
                )
            : [],
          markup: tempPlano.markup,
          markup_mode: tempPlano.markup_mode,
          products: planoProducts,
          commission_distribution: planoCommissions,
          commission_lifetime_other_installments:
            tempPlano.commission_lifetime_other_installments,
          plan_price: planoProducts.reduce(
            (total, product) => total + (product.premium_value || 0),
            0
          ),
          selectedPartners: projectType === "affinity" ? selectedPartners : undefined,
          installments:
            projectType === "affinity" || projectType === "affiliate"
              ? tempPlano.installments
              : undefined,
          otherInstallments:
            projectType === "affinity" || projectType === "affiliate"
              ? tempPlano.otherInstallments
              : undefined,
        };

        // Atualizar plano existente no formulário
        form.setValue(`planos.${editingPlanoIndex}`, novoPlano);

        // Fechar modal e, se estiver em fluxo de edição, disparar atualização via mutation
        setModalOpen(false);

        if (isEditing && typeof submitUpdatePlan === "function") {
          submitUpdatePlan(editingPlanoIndex);
        }
      }
    }
  };

  if (!open) return null;

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="tab1">Produtos</TabsTrigger>
          <TabsTrigger value="tab3">Menu Selling</TabsTrigger>
        </TabsList>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((data) => onSubmit(data))} className="space-y-4">
            <TabsContent value="tab1">
              <Card className="p-4">
                <CardHeader>
                  <CardTitle>Produtos</CardTitle>
                  <CardDescription className="space-y-2">
                    <span className="block">
                      Selecione os produtos que serão oferecidos ao cliente
                    </span>
                    <span className="block text-muted-foreground">
                      {t("plansForm.sameGatewayHint")}
                    </span>
                    {planGatewayLockId ? (
                      <span className="block text-sm border-l-2 border-primary pl-3 py-1">
                        {t("plansForm.sameGatewayFilterActive")}
                        {lockedGatewayLabel ? (
                          <span className="font-medium"> {lockedGatewayLabel}</span>
                        ) : null}
                      </span>
                    ) : null}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mb-4">
                    <FormField
                      control={searchForm.control}
                      name="product_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="product_name">
                            {"Pesquisar produtos pelo nome"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              id="product_name"
                              placeholder=" "
                              onChange={(e) => {
                                field.onChange(e);
                                if (
                                  !e.target.value ||
                                  e.target.value.trim() === ""
                                ) {
                                  clearProductsFilter();
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSearchProducts();
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      variant={"default"}
                      className="w-fit mt-7"
                      type="button"
                      onClick={handleSearchProducts}
                    >
                      Buscar produtos
                    </Button>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Selecionar</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Tipo de Vigência</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productsForPlanBuilderTable?.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center text-gray-500 py-8"
                          >
                            {planGatewayLockId &&
                            (filteredProducts?.length ?? 0) > 0
                              ? t("plansForm.emptyFilteredByGateway")
                              : searchForm.getValues("product_name")
                                ? t("plansForm.emptySearch")
                                : t("plansForm.emptyNoProducts")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productsForPlanBuilderTable?.map((p, i) => (
                          <Collapsible
                            key={p.id}
                            asChild
                            open={openRowId === p.id}
                            onOpenChange={() => handleRowToggle(p.id)}
                          >
                            <>
                              <TableRow key={i}>
                                <TableCell>
                                  <CollapsibleTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                    >
                                      {openRowId === p.id ? (
                                        <MinusIcon className="h-4 w-4" />
                                      ) : (
                                        <PlusIcon className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </CollapsibleTrigger>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col gap-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="font-medium">{p.name}</span>
                                      {isInfinitySyncedProduct(p) && (
                                        <Badge
                                          variant="outline"
                                          className="shrink-0 gap-1 text-xs font-normal border-primary/40 text-primary"
                                        >
                                          <RefreshCw className="h-3 w-3" />
                                          {t("products.sync.badge")}
                                        </Badge>
                                      )}
                                    </div>
                                    {isInfinitySyncedProduct(p) &&
                                      (p.infinity_sync_source_organizations?.length ?? 0) > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                          {p.infinity_sync_source_organizations!.map((label) => (
                                            <span
                                              key={label}
                                              className="inline-flex max-w-full rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                                              title={label}
                                            >
                                              {label}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {t(`product.type.${p.type}`)}
                                </TableCell>
                                <TableCell>
                                  {t(
                                    `product.type_of_vigency.${p.type_of_vigency}`
                                  )}
                                </TableCell>
                              </TableRow>
                              <CollapsibleContent>
                                <div className="p-4 bg-gray-50">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {p.capital_ranges.map((capital) => (
                                      <Card key={capital.id} className="p-4">
                                        <CardContent className="p-0">
                                          <div className="space-y-2">
                                            <div className="flex items-center justify-between mb-3">
                                              <Checkbox
                                                checked={isProductSelected(
                                                  capital.id
                                                )}
                                                onCheckedChange={() =>
                                                  handleProductSelection(p, {
                                                    id: capital.id,
                                                    product_id: p.id,
                                                    capital_value:
                                                      capital.capital_value,
                                                    premium_value:
                                                      capital.premium_value,
                                                    description:
                                                      capital.description || "",
                                                    minimum_price:
                                                      (capital as any)
                                                        .minimum_price ?? null,
                                                    plan_name:
                                                      (capital as any)
                                                        .plan_name ?? null,
                                                  })
                                                }
                                                className="mr-2"
                                              />
                                              <span className="text-sm font-medium text-gray-600">
                                                Selecionar
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                              <span className="text-sm font-medium text-gray-600">
                                                {p.type ===
                                                EProductType.INSURANCE_ASSISTANCE
                                                  ? "Valor mínimo:"
                                                  : "Capital:"}
                                              </span>
                                              <span className="text-sm font-semibold text-green-600">
                                                {formatPrice(
                                                  p.type ===
                                                    EProductType.INSURANCE_ASSISTANCE
                                                    ? capital.minimum_price ?? 0
                                                    : capital.capital_value
                                                )}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                              <span className="text-sm font-medium text-gray-600">
                                                {p.type ===
                                                EProductType.INSURANCE_ASSISTANCE
                                                  ? "Valor de custo:"
                                                  : "Prêmio:"}
                                              </span>
                                              <span className="text-sm font-semibold text-blue-600">
                                                {formatPrice(
                                                  capital.premium_value
                                                )}
                                              </span>
                                            </div>
                                            <div className="pt-2 border-t border-gray-200">
                                              <p className="text-xs text-gray-600">
                                                <span className="text-gray-500">
                                                  Nome:{" "}
                                                </span>
                                                {
                                                  ((capital as any).plan_name ||
                                                    "Sem nome") as string
                                                }
                                              </p>
                                              <p className="text-xs text-gray-500 mt-1">
                                                <span className="text-gray-500">
                                                  Descrição:{" "}
                                                </span>
                                                {capital.description ||
                                                  "Sem descrição"}
                                              </p>
                                            </div>
                                          </div>
                                        </CardContent>
                                      </Card>
                                    ))}
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </>
                          </Collapsible>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Seção de produtos selecionados */}
              {selectedProductsWithCapitalRanges.length > 0 && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle>Produtos Selecionados</CardTitle>
                    <CardDescription>
                      Produtos com capital ranges selecionados para uso nos
                      planos
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      {selectedProductsWithCapitalRanges.map(
                        (selectedProduct) => (
                          <Card
                            key={selectedProduct.id}
                            className="p-4 border-green-200 bg-green-50"
                          >
                            <CardContent className="p-0">
                              <div className="space-y-2">
                                <div className="pt-2 border-gray-200">
                                  <p className="text-xs text-gray-500">
                                    {selectedProduct.plan_name ||
                                      selectedProduct.name}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Tipo:{" "}
                                    {t(`product.type.${selectedProduct.type}`)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Vigência:{" "}
                                    {t(
                                      `product.type_of_vigency.${selectedProduct.type_of_vigency}`
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {selectedProduct.description ||
                                      "Sem descrição"}
                                  </p>
                                </div>
                                <div className="flex justify-between items-center border-t">
                                  <span className="text-sm font-medium text-gray-600">
                                    {selectedProduct.type ===
                                    EProductType.INSURANCE_ASSISTANCE
                                      ? "Valor mínimo:"
                                      : "Capital:"}
                                  </span>
                                  <span className="text-sm font-semibold text-green-600">
                                    {formatPrice(
                                      selectedProduct.type ===
                                        EProductType.INSURANCE_ASSISTANCE
                                        ? selectedProduct.minimum_price ??
                                            selectedProduct.capital_value
                                        : selectedProduct.capital_value
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-600">
                                    {selectedProduct.type ===
                                    EProductType.INSURANCE_ASSISTANCE
                                      ? "Valor de custo:"
                                      : "Prêmio:"}
                                  </span>
                                  <span className="text-sm font-semibold text-blue-600">
                                    {formatPrice(selectedProduct.premium_value)}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              <div className="flex justify-end">
                <Button className="mt-4" onClick={() => setActiveTab("tab3")}>
                  Ir para Menu Selling
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="tab3">
              <Card className="p-4">
                <CardHeader>
                  <CardTitle>Planos</CardTitle>
                  <CardDescription>
                    Monte os planos usando os produtos selecionados na aba
                    Produtos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <Button
                      type="button"
                      onClick={handleOpenNewPlanoModal}
                      variant="default"
                      className="w-fit"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Novo Plano
                    </Button>
                  </div>

                  {/* Tabela dos planos criados */}
                  {(() => {
                    const planos = form.watch("planos") || [];
                    const planosValidos = planos.filter(
                      (plano) =>
                        plano &&
                        plano.name &&
                        plano.name.trim() !== "" &&
                        plano.products &&
                        plano.products.length > 0
                    );

                    if (planosValidos.length === 0) {
                      return (
                        <div className="text-center text-gray-500 py-8">
                          Nenhum plano criado ainda. Clique em "Adicionar Novo
                          Plano" para começar.
                        </div>
                      );
                    }

                    return (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome do Plano</TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead>Características</TableHead>
                            <TableHead>Quantidade de Produtos</TableHead>
                            <TableHead>Prêmio Total</TableHead>
                            <TableHead>Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {planosValidos.map((plano, index) => {
                            const produtosSelecionados = plano.products || [];
                            const precoTotal = produtosSelecionados.reduce(
                              (total, product) => {
                                // Agora product é um objeto com premium_value
                                return total + (product.premium_value || 0);
                              },
                              0
                            );

                            return (
                              <TableRow key={plano.id || index}>
                                <TableCell className="font-medium">
                                  {plano.name}
                                </TableCell>
                                <TableCell>
                                  {plano.description || "Sem descrição"}
                                </TableCell>
                                <TableCell>
                                  {plano.plan_features &&
                                  plano.plan_features.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {plano.plan_features.map(
                                        (feature, featureIndex) => (
                                          <span
                                            key={featureIndex}
                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                          >
                                            {feature}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">
                                      Nenhuma característica
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {produtosSelecionados.length} produtos
                                </TableCell>
                                <TableCell className="font-medium text-green-600">
                                  {formatPrice(precoTotal)}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditPlano(index)}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Editar
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    );
                  })()}
                </CardContent>
              </Card>
              <div className="flex justify-end">
                {isEditing ? (
                  <Button
                    type="button"
                    className="mt-4"
                    onClick={() => {
                      if (submitUpdatePlan) {
                        submitUpdatePlan(0);
                      }
                    }}
                  >
                    Salvar alterações
                  </Button>
                ) : (
                  <Button type="submit" className="mt-4">
                    Cadastrar planos
                  </Button>
                )}
              </div>
            </TabsContent>

            {/* Modal de plano */}
            <PlanoModal
              open={modalOpen}
              onOpenChange={setModalOpen}
              tempPlano={tempPlano}
              setTempPlano={setTempPlano}
              selectedProducts={productsForModal}
              editingPlanoIndex={editingPlanoIndex}
              onSave={handleSavePlano}
              projectType={projectType}
              projectPartners={projectPartners}
            />
          </form>
        </Form>
      </Tabs>
    </>
  );
};
